import { postDailyReport } from '@/api/reports'
import { getBonuses, getEmployeeByMyoctansId, getProjectsWithTask, getTasks, getSettings } from '@/api/resources'
import Btn from '@/components/forms/Btn'
import Comments from '@/components/forms/Comments'
import Drawing, { useDrawing } from '@/components/forms/Drawing'
import Input from '@/components/forms/Input'
import DatePicker, { registerLocale } from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { fr, enUS } from 'date-fns/locale'
import { AddressSelect, EquipmentSelect, ProjectWithTaskSelect, RelatedEquipmentSelect, TaskSelect } from '@/components/forms/FuzzySelect'
import { INPUT_DATETIME_FMT, INPUT_DATETIME_FMT_FULL } from '@/constants'
import globalContext from '@/context'
import { useFilledDailyReport, useReports, useSubmit } from '@/hooks'
import { formDataToJson, hoursToTime, appendFormData, isNumeric } from '@/utils'
import { groupBy } from 'lodash-es'
import { DateTime } from 'luxon'
import type { FunctionComponent as FC } from 'preact'
import { useContext, useEffect, useState, useMemo } from 'preact/hooks'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import Checkbox from '@/components/forms/Checkbox'
import { route } from 'preact-router'
import CollapsibleFieldset from '@/components/CollapsibleFieldset'
import DynamicFieldset from '@/components/forms/DynamicFieldset'

type DailyTaskForm = {
	id: number | string | undefined,
	project_id: number | undefined,
	created_by: number | undefined,
	date: DateTime,
	timeEntries: TaskEntry[],
	productionReports: Api.Report[],
	equipment: EquipmentEntry[],
	comment: string,
	gen_mode: string,
	bonuses: Api.Bonus[],
	shift?: 'day' | 'night'
}

type EquipmentEntry = {
	[key: string]: number | string | undefined
	equipment_id: number | undefined,
	quantity: number | undefined,
	start_time: number | undefined,
	end_time: number | undefined,
	fuel: number | undefined,
	comment: string
}

const defaultEquipmentEntry: EquipmentEntry = {
	equipment_id: undefined,
	quantity: 0,
	start_time: 0,
	end_time: 0,
	fuel: undefined,
	comment: ''
}

type TaskEntry = {
	id: number,
	task_id: number,
	myoctans_task_id: number,
	BT: string,
	work_order_time: number,
	traveling_time: number,
	time: number,
	pay_time: number,
	applicable_bonuses: Api.Bonus[]
	[key: string]: number | string | Api.Bonus[] | undefined
}

type Props = {
	path: string
	report_id?: number
	disabled?: boolean
}

const roundToNearest15 = (dt: DateTime) => {
	const minutes = dt.minute;
	const rounded = Math.round(minutes / 15) * 15;
	return dt.set({ minute: rounded === 60 ? 0 : rounded, second: 0, millisecond: 0 }).plus({ hour: rounded === 60 ? 1 : 0 });
};

const DailyTasks: FC<Props> = (p) => {
	const { i18n } = useTranslation('forms')
	useEffect(() => {
		if (i18n.language === 'fr') {
			registerLocale('fr', fr)
		}
	}, [i18n.language])
	const { data: settings, isLoading: settingsLoading, isFetching } = useQuery({
		queryKey: ['settings'],
		queryFn: getSettings,
		staleTime: 0,
		refetchInterval: 30 * 1000,
		cacheTime: 5 * 60 * 1000,
		refetchOnMount: true,
		refetchOnWindowFocus: true,
	});

	const isSettingEnabled = (
		settings: { name: string; value: any }[] | undefined,
		name: string
	): boolean =>
		settings?.some(s => s.name === name && Number(s.value) === 1) ?? false

	const allowTomorrowReports = useMemo(() =>
		isSettingEnabled(settings, 'allow_tomorrow_reports')
		, [settings]);

	if (settingsLoading) {
		return <div>Loading settings...</div>;
	}
	const isDailyTasks = p.path.includes('daily_tasks')
	const { t } = useTranslation('daily_tasks')
	const queryClient = useQueryClient()
	const [state, actions] = useContext(globalContext)
	const filledDailyReport = useFilledDailyReport(p.report_id)
	const { data: reports } = useReports()
	const { data: tasks } = useQuery({
		queryKey: ['tasks'],
		queryFn: getTasks,
	})

	const { data: bonuses } = useQuery({
		queryKey: ['bonuses'],
		queryFn: getBonuses,
	})

	const { data: employee } = useQuery({
		queryKey: ['employee', state?.user?.id],
		queryFn: () => getEmployeeByMyoctansId(state?.user?.id as number),
	})

	const { data: projects } = useQuery({
		queryKey: ['projects'],
		queryFn: getProjectsWithTask,
	})

	const [dailyReport, setDailyReport] = useState<DailyTaskForm>({
		id: crypto.randomUUID(),
		project_id: undefined,
		created_by: state?.user?.id,
		date: roundToNearest15(DateTime.now()),
		timeEntries: [],
		productionReports: [],
		equipment: [],
		comment: '',
		gen_mode: 'simplified',
		bonuses: []
	})

	useEffect(() => {
		if (!filledDailyReport || !tasks || !bonuses) return
		const timeEntries = tasks?.filter((task: any) => !task.is_archived)
			.filter((task: any) => task.project_id == filledDailyReport.project_id)
			.map((task: any) => {
				const entry = filledDailyReport?.time_entries?.find((time_entry: any) => time_entry?.task?.myoctans_task_id == task.myoctans_task_id || time_entry?.myoctans_task_id == task.myoctans_task_id)

				let workOrderTime = 0;
				if (entry && isNumeric(filledDailyReport.id)) {
					workOrderTime = entry.work_order_time / 60;
				} else if (entry) {
					workOrderTime = entry.work_order_time;
				}
				let travelingTime = 0;
				if (entry && isNumeric(filledDailyReport.id)) {
					travelingTime = entry.traveling_time / 60;
				} else if (entry) {
					travelingTime = entry.traveling_time;
				}
				return {
					id: entry?.id ?? null,
					task_id: task.id,
					myoctans_task_id: task.myoctans_task_id,
					BT: task.BT,
					work_order_time: workOrderTime,
					traveling_time: travelingTime,
				}
			})

		const equipments = filledDailyReport?.equipment?.map((equipment: any) => {
			return {
				equipment_id: equipment?.id ?? equipment?.equipment_id,
				quantity: equipment.pivot?.quantity ?? equipment?.quantity,
				start_time: equipment.pivot?.start_time ?? equipment?.start_time,
				end_time: equipment.pivot?.end_time ?? equipment?.end_time,
				fuel: equipment.pivot?.fuel ?? equipment?.fuel,
				comment: equipment.pivot?.comment ?? equipment?.comment
			}
		})

		const productionReports = filledDailyReport?.forms?.map((report: any) => {
			return {
				...report,
				checked: true
			}
		})

		let allBonuses: Api.Bonus[] = [];
		filledDailyReport?.time_entries?.forEach((entry) => {
			if (!isNumeric(filledDailyReport.id)) {
				const bonuses = [];
				entry.bonuses?.forEach((bonus) => {
					const customPrice = Number.parseFloat(entry[`bonus_price_${bonus}`]);
					bonuses.push({
						id: bonus,
						pivot: {
							price: customPrice
						}
					});
				});
				allBonuses = allBonuses.concat(bonuses);
			} else {
				if (entry.bonuses) {
					allBonuses = allBonuses.concat(entry.bonuses);
				}
			}
		})

		const uniqueBonuses = allBonuses.reduce((acc: Api.Bonus[], current) => {
			const x = acc.find(item => item.id === current.id);
			if (!x) {
				return acc.concat([current]);
			} else {
				return acc;
			}
		}, [] as Api.Bonus[])

		let defaultDate = null;
		let defaultShift = null;
		if (productionReports && productionReports.length > 0) {
			const firstChecked = productionReports.find((pr: any) => pr.checked);
			if (firstChecked) {
				if (firstChecked.date_in) {
					defaultDate = DateTime.fromFormat(firstChecked.date_in, INPUT_DATETIME_FMT_FULL);
				}
				if (firstChecked.form?.quarter) {
					defaultShift = firstChecked.form.quarter;
				} else if (firstChecked.form?.work_shift) {
					defaultShift = firstChecked.form.work_shift;
				}
			}
		}

		const firstTimeEntry = filledDailyReport?.time_entries[0];

		let reportDate = defaultDate || DateTime.fromFormat(firstTimeEntry.date_in, INPUT_DATETIME_FMT_FULL);
		reportDate = roundToNearest15(reportDate);
		const newDailyReport: DailyTaskForm = {
			id: filledDailyReport?.id,
			project_id: filledDailyReport?.projects[0]?.id,
			date: reportDate,
			timeEntries: timeEntries,
			equipment: equipments ?? [],
			created_by: filledDailyReport?.created_by,
			productionReports: productionReports ?? [],
			comment: filledDailyReport?.comment ?? '',
			gen_mode: filledDailyReport?.gen_mode ?? 'simplified',
			bonuses: uniqueBonuses,
			related_equipment_id: filledDailyReport?.related_equipment_id ?? undefined,
			work_order_numbers: filledDailyReport?.work_order_numbers ?? undefined,
			shift: defaultShift || filledDailyReport?.work_shift || 'day'
		}
		setDailyReport(newDailyReport)
	}, [filledDailyReport, tasks, bonuses])

	useEffect(() => {
		window.scrollTo(0, 0);
	}, []);

	useEffect(() => {
		if (filledDailyReport) return
		const { reports } = getFilteredReports(dailyReport.project_id as number, dailyReport.date)
		setDailyReport({ ...dailyReport, productionReports: reports })
	}, [reports])

	const { setBlob: setEmployeeSignature, append: appendEmployeeSignature } = useDrawing(
		'employee_signature',
		'employee_signature.png'
	)
	const { setBlob: setSupervisorSignature, append: appendSupervisorSignature } = useDrawing(
		'supervisor_signature',
		'supervisor_signature.png'
	)
	const type = 'daily_tasks'

	const { handleSubmit, submitDisabled } = useSubmit({
		async process(fd: FormData) {
			appendEmployeeSignature(fd)
			appendSupervisorSignature(fd)

			if (fd.get('comment')?.toString().trim().length === 0) fd.delete('comment')
		},
		submit: postDailyReport,
		async onSuccess(res) {
			actions.toastInfo(t('toasts:form_submitted'));
			setTimeout(() => {
				history.pushState({}, '', '/reports');
				location.href = '/reports';
			}, 1000);
		}
	})

	const mutation = useMutation({
		mutationKey: ['create-daily-report', dailyReport.id],
		mutationFn: (data: any) => {
			const formData = new FormData()

			appendFormData(formData, data);
			formData.delete('id');
			return postDailyReport(formData)
		},
		onSuccess: (data, variables, context) => {
			if (context?.optimisticDailyReport?.status === 'pending') {
				actions.toastInfo(t('toasts:offline_form_sent'));
			} else {
				actions.toastInfo(t('toasts:form_submitted'));
				setTimeout(() => {
					route('/reports');
				}, 1000);
			}
			queryClient.invalidateQueries({
				queryKey: ['daily-reports']
			})
		},
		onError: (error, variables, context) => {
			actions.toastError(t('toasts:generic_error'))
			setTimeout(() => {
				route('/reports')
			}, 1000)
		}
	})

	const onSubmit = (event: SubmitEvent) => {
		event.preventDefault()

		if (mutation.isPending) {
			return;
		}

		const formData = new FormData(event.currentTarget as HTMLFormElement)

		appendEmployeeSignature(formData)
		appendSupervisorSignature(formData)
		if (formData.get('comment')?.toString().trim().length === 0) formData.delete('comment')

		if (dailyReport?.date) {
			formData.set('shift_start_time', dailyReport.date.toFormat(INPUT_DATETIME_FMT_FULL));
		}

		mutation.mutate(formDataToJson(formData))
	}

	const handleProjectMatch = (project: object | undefined) => {
		if (!project || filledDailyReport) return
		const projectMatch = project as Api.Project
		const timeEntries = projectMatch?.tasks ? projectMatch.tasks.filter(task => !task.is_archived).map(task => {
			return {
				id: task.id,
				task_id: task.id,
				myoctans_task_id: task.myoctans_task_id,
				BT: task.BT,
				work_order_time: 0,
				traveling_time: 0,
				time: 0,
				pay_time: 0,
				applicable_bonuses: task.applicable_bonuses
			}
		}) : []
		const { reports } = getFilteredReports(projectMatch.id as number, dailyReport.date)
		setDailyReport({ ...dailyReport, project_id: projectMatch?.id, timeEntries: timeEntries, productionReports: reports })
	}

	const handleDateInChange = (e: Event) => {
		const input = e.target as HTMLInputElement
		const date = DateTime.fromISO(input.value)
		const { reports } = getFilteredReports(dailyReport.project_id as number, date)
		setDailyReport({ ...dailyReport, date: date, productionReports: reports })
	}

	const handleTaskInput = (e: Event) => {
		const input = e.target as HTMLInputElement
		const task_id = parseInt(input.name.split('[')[1].split(']')[0])
		const field = input.name.split('[')[2].split(']')[0]

		const updatedTimeEntries = dailyReport?.timeEntries.map(entry => {
			if (entry.task_id === task_id) {
				return { ...entry, [field]: parseFloat(input.value) || 0 };
			}
			return entry;
		});
		setDailyReport({ ...dailyReport, timeEntries: updatedTimeEntries });
	}

	const calculateTimes = () => {
		const groupedTimeEntries = dailyReport?.timeEntries.reduce((acc: { [key: string]: number }, entry) => {
			const category = entry.BT;
			if (!acc[category]) {
				acc[category] = 0;
			}
			acc[category] += entry.work_order_time;
			return acc;
		}, {});

		const formattedGroupedTimeEntries = Object.entries(groupedTimeEntries).reduce((acc: { [key: string]: string }, [category, time]) => {
			acc[category] = hoursToTime(time);
			return acc;
		}, {});

		const taskTime = dailyReport?.timeEntries.reduce((acc, entry) => acc + entry.work_order_time, 0)
		return { taskTime: hoursToTime(taskTime), groupedTimeEntries: formattedGroupedTimeEntries }
	}

	const handleReportCheck = (index: number) => {
		const updatedProductionReports = dailyReport?.productionReports.map((report, i) => {
			if (i === index) {
				return { ...report, checked: !report.checked }
			}
			return report
		})

		let updatedTimeEntries = dailyReport?.timeEntries
		updatedTimeEntries = updatedTimeEntries.map(entry => {
			return {
				...entry,
				work_order_time: 0,
				traveling_time: 0
			}
		})

		updatedProductionReports.forEach((report, i) => {
			if (!report.checked) return
			updatedTimeEntries = updatedTimeEntries.map(entry => {
				const task_id = entry.myoctans_task_id
				const timeEntry = report?.form?.time_entries.find(timeEntry => timeEntry.myoctans_task_id == task_id)
				if (timeEntry) {
					entry.work_order_time += (report.checked ? (parseFloat(timeEntry.work_order_time as any) || 0) : 0)
					entry.traveling_time += (report.checked ? (parseFloat(timeEntry.traveling_time as any) || 0) : 0)
				}
				return entry
			})
		})

		const firstChecked = updatedProductionReports.find((pr) => pr.checked);
		let newDate = dailyReport.date;
		let newShift = dailyReport.shift || 'day';

		if (firstChecked) {
			if (firstChecked.date_in) {
				newDate = DateTime.fromFormat(firstChecked.date_in, INPUT_DATETIME_FMT_FULL);
				newDate = roundToNearest15(newDate);
			}
			if (firstChecked.form?.quarter) {
				newShift = firstChecked.form.quarter;
			} else if (firstChecked.form?.work_shift) {
				newShift = firstChecked.form.work_shift;
			} else {
				newShift = 'day';
			}
		} else {
			if (filledDailyReport && filledDailyReport.time_entries && filledDailyReport.time_entries.length > 0) {
				const firstTimeEntry = filledDailyReport.time_entries[0];
				if (firstTimeEntry.date_in) {
					newDate = DateTime.fromFormat(firstTimeEntry.date_in, INPUT_DATETIME_FMT_FULL);
					newDate = roundToNearest15(newDate);
				} else {
					newDate = roundToNearest15(DateTime.now());
				}
				const workShift = filledDailyReport.work_shift;
				if (workShift) {
					newShift = workShift;
				} else {
					newShift = 'day';
				}
			} else {
				newDate = roundToNearest15(DateTime.now());
				newShift = 'day';
			}
		}

		setDailyReport({ ...dailyReport, productionReports: updatedProductionReports, timeEntries: updatedTimeEntries, date: newDate, shift: newShift })
	}

	const { taskTime, groupedTimeEntries } = calculateTimes()

	const getFilteredReports = (projectId: number, date: DateTime) => {
		let filledDrillerProdReports = reports?.filter(report => report.form_type === 'driller_prod')
			.filter(report => report.project_id == projectId)
			.filter(report => {
				let dateIn = DateTime.fromFormat(report.date_in, 'yyyy-MM-dd HH:mm:ss').toISODate()
				if (dateIn == undefined) {
					dateIn = DateTime.fromFormat(report.date_in, 'yyyy-MM-ddTHH:mm').toISODate()
				}
				return dateIn === date.toISODate() ||
					dateIn === date.plus({ days: 1 }).toISODate() ||
					dateIn === date.minus({ days: 1 }).toISODate()
			})
			.filter(report => report.daily_reports.length == 0)
			.filter(report => report.form.is_complete == 1) ?? []

		filledDrillerProdReports = filledDrillerProdReports.map(report => {
			return {
				...report,
				checked: false
			}
		})

		return { reports: filledDrillerProdReports }
	}

	const timeToAllocate = dailyReport.productionReports?.reduce((acc, report) => {
		if (report.checked) {
			return acc + report.form.time_entries?.reduce((acc: number, entry: any) => acc + parseFloat(entry.work_order_time), 0)
		}
		return acc
	}, 0)

	const bonusIndex = dailyReport?.timeEntries.find(entry => entry.work_order_time !== 0)?.task_id ?? dailyReport?.timeEntries[0]?.task_id ?? 0

	const workExecutionAddressId = projects?.find((project) => project.id === dailyReport?.project_id)?.supplier_work_execution_address_id

	return (
		<>
			<h1>{isDailyTasks ? t('title') : t('mechanic_title')}</h1>
			{isNaN(Number(dailyReport.id)) && p.disabled == 'disabled' && (
				<>
					<div className="bg-yellow-200 rounded">
						<p className="p-1">
							{t('forms:this_form_has_not_been_sent')}
						</p>
					</div>
				</>
			)}
			<form onSubmit={onSubmit}>
				<fieldset className='dummy' disabled={p.disabled}>
					<input type="hidden" name="id" value={dailyReport?.id} />
					<input type="hidden" name="type" value={isDailyTasks ? 'daily_tasks' : 'daily_mechanic'} />
					<input type="hidden" name="created_by" value={employee?.user?.id} />
					<input type="hidden" name="date" value={dailyReport?.date?.toISODate()} />
					<input type="hidden" name="project[name]" value={projects?.find((project) => project.id === dailyReport?.project_id)?.name} />
					<input type="hidden" name="shift_start_time" value={dailyReport?.date?.toFormat(INPUT_DATETIME_FMT_FULL)} />

					<label className='span6'>
						{t('date_in_long')}<span class='text-red-600'>&nbsp;*</span>
						<DatePicker
						 wrapperClassName='w-full'
						 selected={dailyReport.date.toJSDate()}
						 onChange={date => {
							 if (!date) return;
							 let dt = DateTime.fromJSDate(date);
							 dt = roundToNearest15(dt);
							 const { reports } = getFilteredReports(dailyReport.project_id as number, dt)
							 setDailyReport({ ...dailyReport, date: dt, productionReports: reports })
						 }}
						 showTimeSelect
						 timeFormat="HH:mm"
						 timeIntervals={15}
						 dateFormat="yyyy-MM-dd' 'HH:mm"
						 maxDate={allowTomorrowReports
							 ? DateTime.now().plus({ days: 1 }).toJSDate()
							 : DateTime.now().toJSDate()}
						 locale={i18n.language === 'fr' ? fr : enUS}
						 required
						/>
					</label>

					<ProjectWithTaskSelect
						required
						name='project_id'
						defaultValue={filledDailyReport ? dailyReport?.project_id?.toString() : undefined}
						label={t('project')}
						onMatch={handleProjectMatch}
					/>

					<AddressSelect
						readonly
						selectedId={workExecutionAddressId}
						name='form[work_execution_address]'
						label={t('work_execution_address')}
						prefered='name'
					/>

					{
						!isDailyTasks && (
							<>
								<RelatedEquipmentSelect
									className='span6'
									name='related_equipment_id'
									label={t('related_equipment')}
									defaultValue={dailyReport?.related_equipment_id?.toString()}
								/>
							</>
						)

					}

					{
						!isDailyTasks && (
							<>
								<DynamicFieldset
									data={dailyReport?.work_order_numbers}
									label={t('maestro_work_orders')}
									make={i => (
										<Input
											className='span6'
											integer
											name={`work_order_numbers[${i}][number]`}
											value={dailyReport?.work_order_numbers?.at(i)?.number}
										/>
									)}
								/>
							</>

						)
					}

					{
						dailyReport?.productionReports?.length > 0 && isDailyTasks ? (
							<fieldset>
								<legend>{t('production_reports')}</legend>
								{dailyReport?.productionReports.map((report, i) => (
									<>
										{report.checked && (
											<input type="hidden" name={`related_forms[]`} value={report.id} />
										)}
										<Checkbox
											class=''
											label={DateTime.fromFormat(report.date_in, 'yyyy-MM-dd hh:mm:ss').toFormat('yyyy-MM-dd hh:mm') + ' - ' + report.form.drilling_block}
											checked={report.checked}
											onChange={() => handleReportCheck(i)}
										/>
									</>
								))}
							</fieldset>
						) : ''
					}

					{
						isDailyTasks && (
							<Input
								className='span6'
								type='text'
								label={t('total_report_hours')}
								value={timeToAllocate}
								readOnly
							/>
						)
					}

					<fieldset>
						<legend>{t('shift')}</legend>
						<div className='flex items-center gap-4'>
							<label className='flex items-center gap-2'>
								<input
									type='radio'
									name='work_shift'
									value='day'
									checked={dailyReport?.shift === 'day' || !dailyReport?.shift}
									onChange={() => setDailyReport({ ...dailyReport, shift: 'day' })}
								/>
								<span style={{ textTransform: 'capitalize' }}>{t('day')}</span>
							</label>
							<label className='flex items-center gap-2'>
								<input
									type='radio'
									name='work_shift'
									value='night'
									checked={dailyReport?.shift === 'night'}
									onChange={() => setDailyReport({ ...dailyReport, shift: 'night' })}
								/>
								<span style={{ textTransform: 'capitalize' }}>{t('night')}</span>
							</label>
						</div>
					</fieldset>

					<fieldset>
						<legend>{t('time_entries')}</legend>
						{dailyReport?.timeEntries ? Object.entries(groupBy(dailyReport.timeEntries, 'BT')).map(([category, tasks]) => (
							<CollapsibleFieldset title={category} defaultCollapsed={true}>
								{tasks.map(task => (
									<>
										<input type="hidden" name={`time_entries[${task.id}][time]`} value={task.work_order_time} />
										<input type="hidden" name={`time_entries[${task.id}][pay_time]`} value={task.work_order_time} />
										<input type="hidden" name={`time_entries[${task.id}][billing_traveling_time]`} value={task.traveling_time} />
										<input type="hidden" name={`time_entries[${task.id}][payable_traveling_time]`} value={task.traveling_time} />
										<input type="hidden" name={`time_entries[${task.id}][task_id]`} value={task.id} />
										<input type="hidden" name={`time_entries[${task.id}][employee_id]`} value={employee?.id} />
										<input type="hidden" name={`time_entries[${task.id}][time_in]`} value={dailyReport.date.toFormat('HH:mm')} />
										<input type="hidden" name={`time_entries[${task.id}][myoctans_type]`} value={'daily_tasks'} />
										<input type="hidden" name={`time_entries[${task.id}][date_in]`} value={dailyReport.date.toFormat('yyyy-MM-dd HH:mm:ss')} />
										{
											dailyReport.bonuses.filter(bonus => (bonus.frequency == 'hourly' && bonus.is_task_restrictive && task.work_order_time != 0 ? task.applicable_bonuses?.find((applicableBonuses: Api.Bonus) => applicableBonuses.id == bonus.id) != undefined : bonus.frequency == 'hourly' && !bonus.is_task_restrictive && task.work_order_time != 0) || task.id == bonusIndex).map((bonus) =>
												<>
													<input type="hidden" name={`time_entries[${task.id}][bonuses][]`} value={bonus.id} />
													<input type="hidden" name={`time_entries[${task.id}][bonus_price_` + bonus.id + ']'} value={bonus.custom_price} />
												</>
											)
										}

										<TaskSelect
											required
											name={`time_entries[${task.id}][myoctans_task_id]`}
											selectedId={task.myoctans_task_id as number}
											label={t('task')}
											readOnly={true}
											className={'span2'}
										/>

										<Input
											label={t('hours')}
											className='span2'
											name={`time_entries[${task.id}][work_order_time]`}
											value={task.work_order_time}
											onBlur={handleTaskInput}
											decimal
										/>
										<Input
											label={t('traveling')}
											className='span2'
											name={`time_entries[${task.id}][traveling_time]`}
											value={task.traveling_time}
											onBlur={handleTaskInput}
											decimal
										/>
									</>
								))}
							</CollapsibleFieldset>
						)) :
							<p class="text-center">{t('select_a_project')}</p>
						}
					</fieldset>

					<fieldset>
						<legend>{t('summary')}</legend>
						{
							groupedTimeEntries ? Object.entries(groupedTimeEntries).map(([category, time]) => (
								parseFloat(time) > 0 && (
									<Input
										className='span6'
										label={category}
										type={'text'}
										disabled
										value={time.toString()}
									/>
								)
							)) : ''
						}
						<Input
							className='span6'
							label={t('task_time')}
							type={'text'}
							disabled
							value={taskTime.toString()}
						/>
						{
							isDailyTasks && (
								<Input
									className='span6'
									label={t('production_report_hours')}
									type={'text'}
									disabled
									value={hoursToTime(timeToAllocate)}
								/>
							)
						}

					</fieldset>

					{
						isDailyTasks && (
							<fieldset>
								<legend>{t('equipments')}</legend>
								<div class='flex flex-col gap-y-8'>
									{dailyReport?.equipment?.map((equipment, i) =>
										<fieldset key={i}>
											<input type="hidden" name={`equipment[${i}][duration]`} value={(equipment.end_time ?? 0) - (equipment.start_time ?? 0)} />
											<EquipmentSelect
												className='span4'
												required
												name={`equipment[${i}][equipment_id]`}
												defaultValue={equipment.equipment_id?.toString()}
												label={t('name')}
												filterkey={'forms'}
											/>
											<Input
												className='span2'
												required
												integer
												value={equipment.quantity}
												name={`equipment[${i}][quantity]`}
												onChange={(event) => {
													const updatedEquipment = [...dailyReport.equipment];
													updatedEquipment[i].quantity = parseInt((event.target as HTMLInputElement)?.value);
													setDailyReport({ ...dailyReport, equipment: updatedEquipment });
												}}
												label={t('quantity')}
											/>
											<Input
												className='span2'
												integer
												value={equipment.start_time}
												name={`equipment[${i}][start_time]`}
												onChange={(event) => {
													const updatedEquipment = [...dailyReport.equipment];
													updatedEquipment[i].start_time = parseInt((event.target as HTMLInputElement)?.value);
													setDailyReport({ ...dailyReport, equipment: updatedEquipment });
												}}
												label={t('start_time')}
											/>
											<Input
												className='span2'
												integer
												value={equipment.end_time}
												name={`equipment[${i}][end_time]`}
												onChange={(event) => {
													const updatedEquipment = [...dailyReport.equipment];
													updatedEquipment[i].end_time = parseInt((event.target as HTMLInputElement)?.value);
													setDailyReport({ ...dailyReport, equipment: updatedEquipment });
												}}
												label={t('end_time')}
											/>
											<Input
												className='span2'
												integer
												value={equipment.fuel}
												name={`equipment[${i}][fuel]`}
												onChange={(event) => {
													const updatedEquipment = [...dailyReport.equipment];
													updatedEquipment[i].fuel = parseInt((event.target as HTMLInputElement)?.value);
													setDailyReport({ ...dailyReport, equipment: updatedEquipment });
												}}
												label={t('fuel')}
											/>
											<Input
												className='span6'
												type='text'
												value={equipment.comment}
												name={`equipment[${i}][comment]`}
												onChange={(event) => {
													const updatedEquipment = [...dailyReport.equipment];
													updatedEquipment[i].comment = (event.target as HTMLInputElement)?.value;
													setDailyReport({ ...dailyReport, equipment: updatedEquipment });
												}}
												label={t('comments')}
											/>

										</fieldset>
									)}
									<div class='flex justify-end gap-x-2'>
										{dailyReport.equipment.length === 0 ? '' :
											<Btn danger onClick={() => setDailyReport({ ...dailyReport, equipment: dailyReport.equipment.slice(0, -1) })}>
												{t('forms:remove')}
											</Btn>
										}
										<Btn onClick={() => setDailyReport({ ...dailyReport, equipment: [...dailyReport.equipment, JSON.parse(JSON.stringify(defaultEquipmentEntry))] })}>
											{t('forms:add')}
										</Btn>

									</div>
								</div>
							</fieldset>
						)
					}

					<br />
					<div class='flex flex-col gap-y-8'>
						<fieldset>
							<legend>{t('bonuses')}</legend>

							{bonuses?.map((bonus, index) => (

								bonus.is_custom ? (
									<>
										<div class={'span3-nb flex items-center'}>
											<input
												type="checkbox"
												key={index}
												value={bonus.id}
												checked={dailyReport.bonuses.find(checkedBonus => checkedBonus.id == bonus.id) ? true : false}
												id={`bonus-${index}`}
												class={'span3-nb'}
												onClick={(e) => {
													const input = e.target as HTMLInputElement
													const previousDailyReport = dailyReport
													const previousBonuses = dailyReport.bonuses
													const selectedBonus = bonuses?.find(bonus => bonus.id == parseInt(input.value))
													if (input.checked && selectedBonus) {
														setDailyReport({ ...previousDailyReport, bonuses: [...previousBonuses, selectedBonus] })
													} else {
														const updatedCheckedBonuses = previousBonuses.filter(bonus => bonus.id != parseInt(input.value))
														setDailyReport({ ...previousDailyReport, bonuses: updatedCheckedBonuses })
													}
												}}
											/>
											<label class="text-xs ml-1" htmlFor={`bonus-${index}`}>
												{bonus.name}
											</label>
										</div>

										<Input
											className='span3-nb'
											decimal
											value={dailyReport.bonuses.find(checkedBonus => checkedBonus.id == bonus.id)?.custom_price || dailyReport.bonuses.find(checkedBonus => checkedBonus.id == bonus.id)?.pivot?.price}
											data-bonus-id={bonus.id}
											label={bonus.name + ' ' + (bonus.category != null ? t('bonus_category_' + bonus.category) : '')}
											disabled={dailyReport.bonuses.find(checkedBonus => checkedBonus.id == bonus.id) == undefined ? true : false}
											onBlur={(e) => {
												const input = e.target as HTMLInputElement

												const updatedBonuses = dailyReport.bonuses.map(bonus =>
													bonus.id == (input.dataset.bonusId as unknown) as number ? { ...bonus, custom_price: parseFloat(input.value) } : bonus
												)
												setDailyReport({ ...dailyReport, bonuses: updatedBonuses })
											}}
										/>
									</>

								) : (
									<>
										<div class={'span3-nb flex items-center'}>
											<input
												type="checkbox"
												key={index}
												value={bonus.id}
												checked={dailyReport.bonuses.find(checkedBonus => checkedBonus.id == bonus.id) ? true : false}
												id={`bonus-${index}`}
												class={'span3-nb'}
												onClick={(e) => {
													const input = e.target as HTMLInputElement
													const previousDailyReport = dailyReport
													const previousBonuses = dailyReport.bonuses
													const selectedBonus = bonuses?.find(bonus => bonus.id == parseInt(input.value))
													if (input.checked && selectedBonus) {
														setDailyReport({ ...previousDailyReport, bonuses: [...previousBonuses, selectedBonus] })
													} else {
														const updatedCheckedBonuses = previousBonuses.filter(bonus => bonus.id != parseInt(input.value))
														setDailyReport({ ...previousDailyReport, bonuses: updatedCheckedBonuses })
													}
												}}
											/>
											<label class="text-xs ml-1" htmlFor={`bonus-${index}`}>
												{bonus.name}
											</label>
										</div>
										<br />

									</>
								)

							))}

						</fieldset>
					</div>
					<br />
					<Comments name='comment' label={t('comments')} />
					<Drawing label={t('employee_signature')} setBlob={setEmployeeSignature} />
					<Drawing label={t('supervisor_signature')} setBlob={setSupervisorSignature} />
					<Btn disabled={mutation.isPending} submit>
						{mutation.isPending ? t('forms:submitting') : t('forms:submit')}
					</Btn>
				</fieldset>
			</form>
		</>


	)
}
export default DailyTasks